import { IServerSideGetRowsParams, SortModelItem } from 'ag-grid-community';
import { pick } from 'lodash';
import moment from 'moment';

import { ColType } from '@app/shared/utils/ag-grid.col-def.utils';

import { AgGridConstants } from '../constants/ag-grid.constants';
import {
  IAgGridFilterModel,
  IAgGridFilterModelValue,
  IBaseGridConstant,
  ICustomFilterBuilderParams,
} from '../interfaces/ag-grid/ag-grid.types';

//

const _DEFAULT_BACKEND_KEY_GETTER = (colId) => colId;

interface _IMapSortOptions {
  defaultSortDirection ?:'asc' | 'desc' | null;
  defaultSortField ?:string;
  /** Allows specifying a getter only when sorting. */
  getBackendSortKeyByColId ?:(colId :string) => string;
}
const _MAP_SORT_OPTION_NAMES :(keyof _IMapSortOptions)[] = [
  'defaultSortDirection',
  'defaultSortField',
  'getBackendSortKeyByColId',
];

const _DEFAULT_MAP_SORT_OPTIONS :_IMapSortOptions = {
  defaultSortDirection: 'desc',
  defaultSortField: 'created_at',
  getBackendSortKeyByColId: _DEFAULT_BACKEND_KEY_GETTER,
};

interface _IMapOptions extends _IMapSortOptions {
  advancedFilters ?:boolean;
  allowMultiValueFilters ?:boolean;
  customFilterBuilders ?:Record<
    string,
    (params :ICustomFilterBuilderParams) => {
      field :string;
      operator :string;
      value :string;
    }
  >;
  disallowFilterFields ?:string[];
  multiSort ?:boolean;
  /**
   * Allows specifying a mapping function that takes the colId and returns the
   * appropriate key for backend filters and sorting.  If filters and sorting
   * need to be different, you'll want to set BOTH `getBackendKeyByColId` AND
   * `getBackendSortKeyByColId`.
   */
  getBackendKeyByColId ?:(colId :string) => string;
}

const _mapMonoSort = (
  sortModel :SortModelItem[],
  options ?:_IMapSortOptions,
) :HttpParamsHash => {
  options = {
    ..._DEFAULT_MAP_SORT_OPTIONS,
    ...options,
  };

  let sortDirection = options.defaultSortDirection;
  let sortField = options.defaultSortField;

  if (sortModel.length > 0) {
    sortDirection = sortModel[0].sort;
    sortField = options.getBackendSortKeyByColId(sortModel[0].colId);
  }

  return { sortDirection, sortField };
};
const _mapMultiSort = (
  sortModel :SortModelItem[],
  options ?:_IMapSortOptions,
) :HttpParamsHash => {
  options = {
    ..._DEFAULT_MAP_SORT_OPTIONS,
    ...options,
  };

  if ((sortModel.length ?? 0) <= 0) {
    return {
      sort: `${options.defaultSortField}:${options.defaultSortDirection}`,
    };
  }

  const sort = sortModel
    .map(({ colId, sort }) => `${options.getBackendSortKeyByColId(colId)}:${sort}`)
    .join(',');

  return { sort };
};

const buildAdvancedFilters = (
  filterModel :IAgGridFilterModelValue,
  options ?:_IMapOptions,
) :{ [key :string] :string | string[] } => {
  options = {
    allowMultiValueFilters: false,
    ..._DEFAULT_MAP_SORT_OPTIONS,
    ...options,
  };

  const _setFilter = (
    acc :{ [key :string] :string | string[] | undefined },
    key :string,
    val :string | number | boolean,
  ) => {
    const value :string = val.toString(); // ensure values are always strings

    if (Array.isArray(acc[key])) {
      (acc[key] as string[]).push(value);
    } else if (options.allowMultiValueFilters && typeof acc[key] === 'string') {
      acc[key] = [acc[key] as string, value];
    } else {
      acc[key] = value;
    }
  };

  return Object.entries(filterModel).reduce(
    (acc, [key, data] :[string, IAgGridFilterModelValue]) => {
      const conditions = data.conditions ?? [data];

      conditions.forEach((condition) => {
        if (options.disallowFilterFields?.includes(key)) return;

        const field = options?.getBackendKeyByColId?.(key) ?? key;

        if (options.customFilterBuilders?.[key]) {
          const customFilter = options.customFilterBuilders[key]({
            field,
            data: condition,
          });

          _setFilter(
            acc,
            `${customFilter.field}[][${customFilter.operator}]`,
            customFilter.value,
          );

          return;
        }

        // add an extra bracket pair, so the server can handle duplicate keys
        const paramKey = `${field}[]`;

        // handle some simple custom filters
        if (typeof condition === 'number' || typeof condition === 'string') {
          _setFilter(acc, paramKey, condition);

          return;
        }

        // NOTE: for more complex custom filters, use the `_IMapOptions.customFilterBuilders` option. [calvin]

        const operator =
          AgGridConstants.AdvancedFilterOperatorByAgGridType[condition.type] ??
          condition.type ??
          'eq';

        if (!condition.type && condition.filterType === 'set') {
          _setFilter(acc, `${paramKey}[in]`, condition.values.join(','));
        } else if (['hasAll', 'notHasAll'].includes(condition.type)) {
          condition.filter
            .split(',')
            .forEach((value) => _setFilter(acc, `${paramKey}[eq]`, value.trim()));
        } else if (['in', 'notIn', 'hasIn', 'notHasIn'].includes(condition.type)) {
          _setFilter(
            acc,
            `${paramKey}[${operator}]`,
            condition.filter.replace(', ', ','),
          ); // strip spaces after commas, which users may have included
        } else if (condition.type === 'inRange') {
          if (condition.filter > condition.filterTo) {
            _setFilter(acc, `${paramKey}[gte]`, condition.filterTo);
            _setFilter(acc, `${paramKey}[lte]`, condition.filter);
          } else {
            _setFilter(acc, `${paramKey}[gte]`, condition.filter);
            _setFilter(acc, `${paramKey}[lte]`, condition.filterTo);
          }
        } else {
          let value = 'true';

          if (condition.filter) {
            value = condition.filter;
          } else if (condition.dateFrom) {
            value = condition.dateFrom;
          }

          _setFilter(acc, `${paramKey}[${operator}]`, value);
        }
      });

      return acc;
    },
    {} as { [key :string] :string | string[] },
  );
};

const mapQueryParams = (
  data :{
    params :IServerSideGetRowsParams;
    paginationPageSize ?:number;
    relations ?:string[];
  },
  options ?:_IMapOptions,
) :any => {
  options = {
    multiSort: false,
    getBackendKeyByColId: null,
    ...options,
  };

  // ensure our getters are always functions.
  options.getBackendKeyByColId ??= _DEFAULT_BACKEND_KEY_GETTER;
  options.getBackendSortKeyByColId ??= options.getBackendKeyByColId;

  const { params, paginationPageSize = 10, relations = [] } = data;
  const filterModel = params?.request?.filterModel ?? {};

  const limit = paginationPageSize;
  const page = params?.request?.startRow ? params.request.startRow / limit + 1 : 1;

  let filters :{ [key :string] :string | string[] | undefined } = {};

  if (Object.keys(filterModel).length > 0) {
    if (options.advancedFilters) {
      filters = buildAdvancedFilters(filterModel, options);
    } else {
      // formats filter model to match the expected format { key: value }
      filters = Object.keys(filterModel).reduce((acc, key) => {
        const backendKey = options.getBackendKeyByColId(key);

        // Check if filterModel[key].filter is an object
        if (filterModel[key].values && Array.isArray(filterModel[key].values)) {
          acc[backendKey] = filterModel[key].values.join(',');
        } else if (filterModel[key]?.filterType === ColType.DATE.toLowerCase()) {
          acc[backendKey] = moment(filterModel[key]?.dateFrom).format('YYYY-MM-DD');
        } else if (filterModel[key].filter) {
          acc[backendKey] = filterModel[key].filter;
        } else if (filterModel[key].type === 'blank') {
          acc[backendKey] = '_BLANK';
        } else if (filterModel[key].type === 'notBlank') {
          acc[backendKey] = '_NOT_BLANK';
        } else if (filterModel[key] !== undefined && filterModel[key] !== 'None') {
          acc[backendKey] = filterModel[key].filter || filterModel[key];
        }

        return acc;
      }, {});
    }
  }

  const sortOptions = pick(options, _MAP_SORT_OPTION_NAMES);
  const sort = options.multiSort
    ? _mapMultiSort(params?.request?.sortModel ?? [], sortOptions)
    : _mapMonoSort(params?.request?.sortModel ?? [], sortOptions);

  return {
    ...filters,
    limit: limit.toString(),
    page: page.toString(),
    relations: relations.join(','),
    ...sort,
  };
};

/**
 * Maps AG Grid filter and sorting model into API request parameters.
 *
 * @param params - AG Grid server-side params
 * @param gridConstants - Grid-specific constants for sorting, filters, and relations
 * @param defaultSortField - Default field to sort by (e.g., 'id')
 * @param paginationSize - Default page size
 * @returns API-ready query parameters
 */
const mapAgGridRequestParams = <T extends string>(
  params :IServerSideGetRowsParams,
  gridConstants :IBaseGridConstant<T>,
  defaultSortField :string = 'id',
  paginationSize :number = 50,
) :{ [key :string] :string | string[] } => {
  const filterModel = params?.request?.filterModel ?? {};
  const sortModel = params?.request?.sortModel ?? [];

  let sortDirection = 'desc';
  let sortField = defaultSortField;

  // Pagination
  const limit = paginationSize;
  const page = params?.request?.startRow ? params.request.startRow / limit + 1 : 1;

  // Sorting
  if (sortModel?.length > 0) {
    sortDirection = sortModel[0].sort;
    sortField =
      gridConstants.sortingApiParamsMapping[sortModel[0].colId] || defaultSortField;
  }

  // Use the filter transformer function to structure the filters
  const filterQueryParams = mapFilterModelToQueryParams(
    gridConstants,
    filterModel,
    gridConstants.topLevelFilters || [],
  );

  return {
    limit: limit.toString(),
    page: page.toString(),
    sortDirection,
    sortField,
    relations: gridConstants.defaultRelations.join('|'),
    ...filterQueryParams, // Spread transformed filters into root params
  };
};

const mapFilterModelToQueryParams = <T extends string>(
  gridConstants :IBaseGridConstant<T>,
  filterModel :IAgGridFilterModel,
  topLevelFilters :string[] = [],
) :Record<string, string | string[]> => {
  const queryObject :Record<string, string[]> = {};
  const filtersArray :string[] = [];

  for (const column in filterModel) {
    if (Object.hasOwn(filterModel, column)) {
      const filter = filterModel[column];

      if ((filter && 'type' in filter) || (filter && 'filterType' in filter)) {
        const mappedColumn = gridConstants.filterApiParamsMapping[column] || column; // Map column to API field
        const operator =
          AgGridConstants.filterModelTypeToBackendOperator[filter.type];
        const filterValue = filter.filter ?? filter.dateFrom; // Support dateFrom for date filters

        // Handle Date Filters with `dateFrom` and `dateTo`
        if (filter.filterType === 'date') {
          const dateFromFilter = moment(filter.dateFrom).format('YYYY-MM-DD');
          const dateToFilter = moment(filter.dateTo).format('YYYY-MM-DD');

          if (filter.dateFrom && filter.dateTo) {
            // Handle date range as `gte` and `lte`
            const gteFilter = `${mappedColumn}|gte|${moment(dateFromFilter).format('YYYY-MM-DD')}`;
            const lteFilter = `${mappedColumn}|lte|${moment(dateToFilter).format('YYYY-MM-DD')}`;

            if (topLevelFilters.includes(column)) {
              queryObject[mappedColumn] = [`${dateFromFilter},${dateToFilter}`];
            } else {
              filtersArray.push(gteFilter, lteFilter);
            }
          } else if (filter.dateFrom) {
            // Remove time if the filter type is "equals"
            const dateOperator = operator ?? 'eq';

            if (topLevelFilters.includes(column)) {
              queryObject[mappedColumn] = [dateFromFilter];
            } else {
              filtersArray.push(
                `${mappedColumn}|${dateOperator}|${dateFromFilter}`,
              );
            }
          }
          continue; // Skip normal filter processing since we've handled date filters
        }

        if (filter.filterType === 'set' && filter.values.length > 0) {
          // Handle set filters
          const setFilter = `${mappedColumn}|in|${filter.values.join(',')}`;

          if (topLevelFilters.includes(column)) {
            queryObject[mappedColumn] = [setFilter];
          } else {
            filtersArray.push(setFilter);
          }

          continue;
        }

        if (filter.filterType === 'text' && filter.type) {
          const operator =
            AgGridConstants.filterModelTypeToBackendOperator[filter.type];
          // Handle set filters
          const filterValue = `${mappedColumn}|${operator}|${filter.filter}`;

          if (topLevelFilters.includes(column)) {
            queryObject[mappedColumn] = [filterValue];
          } else {
            filtersArray.push(filterValue);
          }

          continue;
        }

        // Handle "inRange" filters (for numbers)
        if (filter.type === 'inRange' && filter.filterTo !== undefined) {
          const gteFilter = `${mappedColumn}|gte|${filter.filter}`;
          const lteFilter = `${mappedColumn}|lte|${filter.filterTo}`;

          if (topLevelFilters.includes(column)) {
            queryObject[mappedColumn] = [`${filter.filter},${filter.filterTo}`];
          } else {
            filtersArray.push(gteFilter, lteFilter);
          }
          continue;
        }

        // Standard filters
        if (operator && filterValue !== undefined) {
          const filterString = `${mappedColumn}|${operator}|${filterValue}`;

          if (topLevelFilters.includes(column)) {
            queryObject[mappedColumn] = [filterValue]; // Move to top level
          } else {
            filtersArray.push(filterString);
          }
        }
      }
    }
  }

  if (filtersArray.length > 0) {
    queryObject['filters[]'] = filtersArray;
  }

  return queryObject;
};

const getFilterOptions = (
  filterType :string,
) :{ options :string[]; default :string } => {
  switch (filterType) {
    case 'agTextColumnFilter':
      return { options: ['contains', 'equals'], default: 'contains' };
    case 'agNumberColumnFilter':
      return {
        options: [
          'equals',
          'lessThan',
          'lessThanOrEqual',
          'greaterThan',
          'greaterThanOrEqual',
          'inRange',
        ],
        default: 'equals',
      };
    case 'agDateColumnFilter':
      return {
        options: [
          'equals',
          'lessThan',
          'lessThanOrEqual',
          'greaterThan',
          'greaterThanOrEqual',
          'inRange',
        ],
        default: 'equals',
      };
    default:
      return { options: ['equals'], default: 'equals' };
  }
};

const addFiltersToRequestParams = (
  requestParams :{ [key :string] :string | string[] },
  filters :{ column :string; operator :string; value :string }[],
) :{ [key :string] :string | string[] } => {
  // Extract existing filters or initialize a new array
  const updatedFilters = [...(requestParams['filters[]'] || [])];

  // Loop through each filter and format it
  filters.forEach(({ column, operator, value }) => {
    updatedFilters.push(`${column}|${operator}|${value}`);
  });

  // Return updated requestParams with the new filters array
  return { ...requestParams, ['filters[]']: updatedFilters };
};

export const AgGridUtils = {
  buildAdvancedFilters,
  mapQueryParams,
  mapAgGridRequestParams,
  mapFilterModelToQueryParams,
  getFilterOptions,
  addFiltersToRequestParams,
};
